<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import TreeViewDreLinha from '@/components/Financeiro/relatorios/dre/TreeViewDreLinha.vue'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'
import { ST } from '@/constants/ST'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idDreLayout: 0,
      nome: '',
      descricao: '',
      mascaraFormatacao: '',
      dataInicioVigencia: new Date().toISOString().split('T')[0],
      dataFimVigencia: '9999-12-31',
      lstStatusLayout: '',
      isTemplateSistema: false,
      idOrganizacao: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const temPermissaoCompleta = computed(() => authStore.pode('DRE_LAYOUTS_COMPLETO'))

// --- Plano de Categoria Financeira vigente ---
const idPlanoCategoriaFinanceira = ref(null)

/** Resolve o plano de categoria financeira ativo e vigente para a data atual */
const resolverPlanoCategoria = async () => {
  try {
    const res = await api.get('/PlanosCategoriasFinanceiras', {
      params: {
        lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Ativo,
        idEmpresa: authStore.empresaAtiva,
        pageSize: 50
      }
    })
    const hoje = new Date()
    const plano = (res.data || []).find(p => {
      if (!p.dataInicioVigencia || !p.dataFimVigencia) return false
      const inicio = new Date(p.dataInicioVigencia + 'T00:00:00')
      const fim = new Date(p.dataFimVigencia + 'T00:00:00')
      return hoje >= inicio && hoje <= fim
    })
    idPlanoCategoriaFinanceira.value = plano ? plano.idPlanoCategoriaFinanceira : null
    if (!idPlanoCategoriaFinanceira.value) {
      console.warn('[DreLayoutCadastro] Nenhum plano ativo vigente encontrado para a data atual.')
    }
  } catch (e) {
    console.warn('[DreLayoutCadastro] Erro ao resolver plano vigente:', e)
    idPlanoCategoriaFinanceira.value = null
  }
}
const isReadOnly = computed(() => {
  return isEditing.value && !temPermissaoCompleta.value
})

// --- Auditoria ---
const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

// --- Formulário Base ---
const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

// --- Lógica de "Novo Layout" vs "Editando" ---
const isLayoutSaved = ref(isEditing.value)
const isSavingLayoutLocal = ref(false)
const isLinhasFrameBlocked = computed(() => !isLayoutSaved.value || isReadOnly.value)

// --- Listas ---
const isLoadingListas = ref(false)
const listas = reactive({})

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['DreLayout', 'DreLinha'])
    Object.assign(listas, result)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar opções.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

// Recarregar listas quando trocar de empresa (admin interno)
watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    carregarListas()
    resolverPlanoCategoria()
  }
})

// --- Accordion State ---
const accordionState = reactive({
  layout: true,
  linhas: false
})

// --- FRAME 1: Layout DRE ---

const salvarLayoutLocalmente = async () => {
  if (isReadOnly.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  const erroVigencia = validarVigencia()
  if (erroVigencia) {
    emit('show-notification', { message: erroVigencia, type: 'error' })
    return
  }

  isSavingLayoutLocal.value = true

  const payload = {
    ...item.value
  }

  try {
    const response = await api.post('/DreLayouts', payload)
    item.value = response.data

    isLayoutSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idDreLayout}`

    emit('show-notification', { message: 'Layout salvo. Agora pode adicionar linhas.', type: 'success' })

    accordionState.linhas = true
    await carregarArvore()
  } catch (error) {
    console.error('Erro ao salvar layout:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar o layout.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingLayoutLocal.value = false
  }
}

const validarVigencia = () => {
  if (!item.value.dataInicioVigencia || !item.value.dataFimVigencia) {
    return 'As datas de início e fim de vigência são obrigatórias.'
  }
  if (item.value.dataInicioVigencia > item.value.dataFimVigencia) {
    return 'A data de início não pode ser posterior à data de fim de vigência.'
  }
  return null
}

const submitFormLayout = async () => {
  if (isReadOnly.value) return

  // GUARDIÃO: Modal de linha aberto → não submeter o formulário de layout
  if (isModalLinhaOpen.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  const erroVigencia = validarVigencia()
  if (erroVigencia) {
    emit('show-notification', { message: erroVigencia, type: 'error' })
    return
  }

  const payload = {
    ...item.value
  }

  await submitForm('DreLayouts', 'idDreLayout', payload)
}

// --- FRAME 2: Linhas do DRE (TreeView) ---
const linhasArvore = ref([])
const isLoadingArvore = ref(false)

const carregarArvore = async () => {
  if (!item.value.idDreLayout) return

  isLoadingArvore.value = true
  try {
    const response = await api.get(`/DreLinhas/arvore/${item.value.idDreLayout}`)
    linhasArvore.value = response.data
  } catch (error) {
    console.error('Erro ao carregar árvore de linhas:', error)
    emit('show-notification', { message: 'Erro ao carregar linhas.', type: 'error' })
  } finally {
    isLoadingArvore.value = false
  }
}

// --- Estado de expansão centralizado ---
const expandidos = ref(new Set())
let isPrimeiraCargaArvore = true

const expandirTudo = () => {
  if (!linhasArvore.value || linhasArvore.value.length === 0) return
  const ids = new Set()
  const percorrer = (linhas) => {
    for (const linha of linhas) {
      if (linha.subLinhas && linha.subLinhas.length > 0) {
        ids.add(linha.idDreLinha)
        percorrer(linha.subLinhas)
      }
    }
  }
  percorrer(linhasArvore.value)
  expandidos.value = ids
}

const colapsarTudo = () => { expandidos.value = new Set() }

watch(linhasArvore, (novaArvore) => {
  if (novaArvore && novaArvore.length > 0) {
    if (isPrimeiraCargaArvore) {
      expandirTudo()
      isPrimeiraCargaArvore = false
    }
  }
}, { immediate: true })

// --- Filtro de linhas ---
const filtroLinhas = ref('')

// --- Preview do Relatório ---
const isPreviewOpen = ref(false)

const linhasParaPreview = computed(() => {
  const resultado = []
  const percorrer = (linhas) => {
    for (const linha of linhas) {
      resultado.push(linha)
      if (linha.subLinhas && linha.subLinhas.length > 0) {
        percorrer(linha.subLinhas)
      }
    }
  }
  percorrer(linhasArvore.value)
  return resultado
})

const abrirPreviewDre = () => {
  isPreviewOpen.value = true
}

const linhasFiltradas = computed(() => {
  const termo = filtroLinhas.value.toLowerCase().trim()
  if (!termo) return linhasArvore.value

  const filtrar = (linhas) => {
    return linhas
      .filter(l => {
        const match = (l.codigoEstruturado && l.codigoEstruturado.toLowerCase().includes(termo)) ||
                      l.nome.toLowerCase().includes(termo)
        const subsMatch = l.subLinhas && filtrar(l.subLinhas).length > 0
        return match || subsMatch
      })
      .map(l => ({
        ...l,
        subLinhas: l.subLinhas ? filtrar(l.subLinhas) : []
      }))
  }
  return filtrar(linhasArvore.value)
})

// --- Handler de mover (drag-and-drop) ---
const onMover = async ({ itemId, destinoId }) => {
  if (!itemId || itemId === destinoId) return
  try {
    const linha = encontrarLinhaNaArvore(linhasArvore.value, itemId)
    await api.put(`/DreLinhas/${itemId}/mover`, {
      novoIdDreLinhaPai: destinoId,
      novaOrdem: linha ? linha.ordem : 1,
      novoNivel: linha ? linha.nivel : 1
    })
    emit('show-notification', { message: 'Linha movida com sucesso.', type: 'success' })
    await carregarArvore()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao mover linha.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Modal de Linha ---
const isModalLinhaOpen = ref(false)
const isEditingLinha = ref(false)

const linhaVazia = {
  idDreLinha: 0,
  idDreLayout: 0,
  idDreLinhaPai: null,
  idCategoriaFinanceira: null,
  codigoEstruturado: '',
  nome: '',
  lstTipoLinha: '',
  lstNaturezaLinha: '',
  ordem: 1,
  nivel: 1,
  exibirNoRelatorio: true,
  nomeLinhaPai: null,
  codigoEstruturadoPai: null
}

const modalLinha = reactive({ ...linhaVazia })

// Configuração de busca de Categoria Financeira
const configBuscaCategoria = computed(() => {
  if (!authStore.organizacaoAtiva) return null
  return criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira',
    null,
    ST.Ativo,
    authStore.organizacaoAtiva,
    null,
    idPlanoCategoriaFinanceira.value,
    null
  )
})

// --- Helpers ---
function getTextoLista (lista, codigo) {
  if (!lista || !codigo) return codigo || '-'
  let item = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  if (item) return item.textoLista
  item = lista.find(i => String(i.idListaSistemaItem) === String(codigo))
  if (item) return item.textoLista
  item = lista.find(i => i.textoLista && i.textoLista.toUpperCase() === codigo.toUpperCase())
  return item ? item.textoLista : codigo
}

// Verifica se o tipo da linha é Analítica (resolve tanto valor raw quanto código da lista)
const isLinhaAnalitica = computed(() => {
  const tipo = modalLinha.lstTipoLinha
  if (!tipo) return true // vazio = novo, mostra campo
  const texto = getTextoLista(listas.LstTipoLinha, tipo)
  return texto === 'Analítica' || texto === 'ANALITICA' || tipo.toUpperCase() === 'ANALITICA'
})

const encontrarLinhaNaArvore = (arvore, id) => {
  for (const linha of arvore) {
    if (linha.idDreLinha === id) return linha
    if (linha.subLinhas && linha.subLinhas.length > 0) {
      const encontrado = encontrarLinhaNaArvore(linha.subLinhas, id)
      if (encontrado) return encontrado
    }
  }
  return null
}

const abrirModalCriarLinha = (paiId = null, nivel = 1) => {
  if (isReadOnly.value) return

  isEditingLinha.value = false

  let nomePai = null
  let codigoEstruturadoPai = null
  if (paiId) {
    const pai = encontrarLinhaNaArvore(linhasArvore.value, paiId)
    if (pai) {
      nomePai = pai.nome
      codigoEstruturadoPai = pai.codigoEstruturado
    }
  }

  Object.assign(modalLinha, {
    ...linhaVazia,
    idDreLayout: item.value.idDreLayout,
    idDreLinhaPai: paiId,
    nivel: paiId ? nivel + 1 : 1,
    nomeLinhaPai: nomePai,
    codigoEstruturadoPai: codigoEstruturadoPai
  })
  isModalLinhaOpen.value = true
}

const abrirModalEditarLinha = async (linha) => {
  if (isReadOnly.value) return

  try {
    const response = await api.get(`/DreLinhas/${linha.idDreLinha}`)
    const dados = response.data

    // Se o backend não retornou os dados do pai, tenta resolver a partir da árvore local
    if (!dados.nomeLinhaPai && linha.idDreLinhaPai) {
      const pai = encontrarLinhaNaArvore(linhasArvore.value, linha.idDreLinhaPai)
      if (pai) {
        dados.nomeLinhaPai = pai.nome
        dados.codigoEstruturadoPai = pai.codigoEstruturado
      }
    }

    Object.assign(modalLinha, dados)
    isEditingLinha.value = true
    isModalLinhaOpen.value = true
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da linha.', type: 'error' })
  }
}

const salvarLinha = async () => {
  if (!modalLinha.nome) {
    emit('show-notification', { message: 'Nome da linha é obrigatório.', type: 'warning' })
    return
  }
  if (!modalLinha.lstTipoLinha) {
    emit('show-notification', { message: 'Tipo da linha é obrigatório.', type: 'warning' })
    return
  }
  if (!modalLinha.lstNaturezaLinha) {
    emit('show-notification', { message: 'Natureza da linha é obrigatória.', type: 'warning' })
    return
  }

  try {
    const payload = { ...modalLinha }
    if (isEditingLinha.value) {
      await api.put(`/DreLinhas/${modalLinha.idDreLinha}`, payload)
      emit('show-notification', { message: 'Linha atualizada com sucesso.', type: 'success' })
    } else {
      await api.post('/DreLinhas', payload)
      emit('show-notification', { message: 'Linha criada com sucesso.', type: 'success' })
    }

    isModalLinhaOpen.value = false

    const idsExpandidosAntes = new Set(expandidos.value)
    const paiId = modalLinha.idDreLinhaPai

    await carregarArvore()

    if (!isEditingLinha.value && paiId) {
      const novosExpandidos = new Set(idsExpandidosAntes)
      novosExpandidos.add(paiId)
      expandidos.value = novosExpandidos
    }
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao salvar linha.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const excluirLinha = async (linha) => {
  if (isReadOnly.value) return

  try {
    await api.delete(`/DreLinhas/${linha.idDreLinha}`)
    emit('show-notification', { message: 'Linha excluída com sucesso.', type: 'success' })
    await carregarArvore()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir linha.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Helpers (apenas badge classes, getTextoLista já foi definido acima) ---
const getTipoLinhaBadgeClass = (tipo) => {
  if (!tipo) return 'bg-secondary'
  const t = tipo.toUpperCase()
  if (t === 'ANALITICA') return 'bg-info text-dark'
  if (t === 'SINTETICA') return 'bg-primary'
  if (t === 'TOTALIZADOR') return 'bg-warning text-dark'
  return 'bg-secondary'
}

const getNaturezaBadgeClass = (natureza) => {
  if (!natureza) return 'bg-secondary'
  const n = natureza.toUpperCase()
  if (n === 'RECEITA') return 'bg-success'
  if (n === 'DESPESA') return 'bg-danger'
  if (n === 'TOTALIZADOR') return 'bg-warning text-dark'
  return 'bg-info text-dark'
}

// Inicialização
onMounted(() => {
  carregarListas()
  resolverPlanoCategoria()
})

let watchLayoutLoaded = false
watch(() => item.value.idDreLayout, (novoId) => {
  if (novoId && novoId > 0) {
    if (watchLayoutLoaded) return
    watchLayoutLoaded = true

    isLayoutSaved.value = true
    accordionState.linhas = true
    carregarArvore()
  }
}, { immediate: true })
</script>

<template>
  <FormularioCadastro
    module-name="Layouts de DRE"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitFormLayout"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
    :desabilitar-salvar="isReadOnly"
    :ocultar-salvar="!isLayoutSaved"
    label-botao-adicionar="Salvar Layout"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- ============================================================ -->
    <!-- FRAME 1: DADOS DO LAYOUT -->
    <!-- ============================================================ -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.layout = !accordionState.layout">
        <h5 class="mb-0"><i class="bi bi-layout-text-window me-2 text-primary"></i> Layout de DRE</h5>
        <div>
          <span v-if="isLayoutSaved" class="badge bg-success text-white me-2 fs-6"><i class="bi bi-check-lg"></i> Layout #{{ item.idDreLayout }}</span>
          <i :class="['bi', accordionState.layout ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
      </div>
      <div class="card-body" v-show="accordionState.layout">
        <div v-if="isLoadingListas" class="alert alert-info py-2 small border-0 shadow-sm">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          A carregar listas de opções...
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="nome"
              label="Nome do Layout"
              v-model="item.nome"
              placeholder="Ex: DRE Gerencial 2026"
              maxlength="100"
              :readonly="isReadOnly"
              :required="true"
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="mascaraFormatacao"
              label="Máscara de Formatação"
              v-model="item.mascaraFormatacao"
              placeholder="Ex: 0.0.00.00"
              maxlength="20"
              :readonly="isReadOnly"
              descricao-informativa="Opcional. Define a estrutura de níveis. Ex: '0.0.00.00' = 4 níveis"
            />
          </div>

          <div class="col-md-12">
            <CampoInput
              id="descricao"
              label="Descrição"
              type="textarea"
              v-model="item.descricao"
              maxlength="255"
              :readonly="isReadOnly"
            />
          </div>

          <div class="col-md-2">
            <CampoInput
              id="dataInicioVigencia"
              label="Data Início Vigência"
              type="date"
              v-model="item.dataInicioVigencia"
              :readonly="isReadOnly"
              :required="true"
            />
          </div>

          <div class="col-md-2">
            <CampoInput
              id="dataFimVigencia"
              label="Data Fim Vigência"
              type="date"
              v-model="item.dataFimVigencia"
              :readonly="isReadOnly"
              :required="true"
              descricao-informativa="Layouts abertos: 31/12/9999"
            />
          </div>

          <div class="col-md-2">
            <CampoBuscaLista
              id="lstStatusLayout"
              label="Status do Layout"
              v-model="item.lstStatusLayout"
              tabela="DreLayout"
              :disabled="isReadOnly"
              :required="true"
              @show-notification="p => emit('show-notification', p)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar Layout e Liberar Linhas (apenas criação) -->
    <div class="mb-4" v-if="!isLayoutSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarLayoutLocalmente"
        :disabled="isSavingLayoutLocal || !item.nome || !item.dataInicioVigencia || !item.dataFimVigencia"
      >
        <span v-if="isSavingLayoutLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Layout e Liberar Linhas
      </button>
    </div>

    <!-- ============================================================ -->
    <!-- FRAME 2: LINHAS DO DRE (TREEVIEW) -->
    <!-- ============================================================ -->
    <div :class="{ 'pe-none opacity-50': isLinhasFrameBlocked }">
      <div class="card mb-4">
        <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center cursor-pointer" @click="accordionState.linhas = !accordionState.linhas">
          <h5 class="mb-0 text-white"><i class="bi bi-list-nested me-2"></i> Linhas do DRE</h5>
          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-light text-primary fs-6" v-if="linhasArvore.length > 0">
              {{ linhasArvore.length }} raiz(es)
            </span>
            <button type="button" class="btn btn-sm btn-light py-0"
                    v-if="isLayoutSaved && linhasArvore.length > 0"
                    @click.stop="abrirPreviewDre"
                    title="Preview do Relatório DRE">
              <i class="bi bi-file-earmark-bar-graph"></i>
            </button>
            <button type="button" class="btn btn-sm btn-light py-0"
                    v-if="isLayoutSaved && !isReadOnly"
                    @click.stop="abrirModalCriarLinha(null, 1)"
                    title="Adicionar linha raiz">
              <i class="bi bi-plus-lg"></i>
            </button>
            <i :class="['bi', accordionState.linhas ? 'bi-chevron-up' : 'bi-chevron-down', 'text-white']"></i>
          </div>
        </div>
        <div class="card-body p-0" v-show="accordionState.linhas">
          <!-- Guardião: Layout não salvo -->
          <div v-if="!isLayoutSaved" class="text-center text-muted py-4">
            <i class="bi bi-info-circle me-2"></i>
            Salve o layout primeiro para gerenciar as linhas do DRE.
          </div>

          <!-- Loading da árvore -->
          <div v-else-if="isLoadingArvore" class="text-center p-4">
            <div class="spinner-border text-primary spinner-border-sm me-2"></div> Carregando linhas...
          </div>

          <!-- TreeView -->
          <div v-else class="p-3">
            <div v-if="linhasArvore.length === 0" class="text-center text-muted py-4">
              Nenhuma linha cadastrada.
              <button type="button" class="btn btn-sm btn-outline-primary ms-3" v-if="!isReadOnly" @click="abrirModalCriarLinha(null, 1)">
                <i class="bi bi-plus-lg me-1"></i>Adicionar Linha Raiz
              </button>
            </div>

            <!-- Filtro -->
            <div class="mb-2" v-if="linhasArvore.length > 0">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-body-tertiary border-secondary-subtle">
                  <i class="bi bi-search"></i>
                </span>
                <input type="text" class="form-control form-control-sm border-secondary-subtle"
                       v-model="filtroLinhas"
                       placeholder="Filtrar por código ou nome..."
                       @input="expandirTudo">
                <button v-if="filtroLinhas" type="button"
                        class="input-group-text bg-body-tertiary border-secondary-subtle"
                        @click="filtroLinhas = ''"
                        title="Limpar filtro">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <!-- Botões de expansão -->
            <div class="d-flex gap-2 mb-2 flex-wrap" v-if="linhasFiltradas.length > 0">
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="expandirTudo">
                <i class="bi bi-arrows-expand me-1"></i>Expandir Tudo
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="colapsarTudo">
                <i class="bi bi-arrows-collapse me-1"></i>Colapsar Tudo
              </button>
            </div>

            <!-- TreeView Recursivo -->
            <TreeViewDreLinha
              v-if="linhasFiltradas.length > 0"
              :linhas="linhasFiltradas"
              :nivel="1"
              :isReadOnly="isReadOnly"
              :expandidos="expandidos"
              :listas="listas"
              @update:expandidos="expandidos = $event"
              @adicionar-filho="({ paiId, nivel }) => abrirModalCriarLinha(paiId, nivel)"
              @editar="abrirModalEditarLinha"
              @excluir="excluirLinha"
              @mover="onMover"
            />

            <!-- Mensagem quando filtro não encontra resultados -->
            <div v-if="linhasArvore.length > 0 && linhasFiltradas.length === 0" class="text-center text-muted py-3">
              <i class="bi bi-search me-1"></i>
              Nenhuma linha corresponde ao filtro "{{ filtroLinhas }}".
            </div>
          </div>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <!-- ============================================================ -->
  <!-- MODAL: ADICIONAR/EDITAR LINHA (FORA DO FormularioCadastro para evitar submit do form) -->
  <!-- ============================================================ -->
  <ModalFormularioV2
    v-model="isModalLinhaOpen"
    :titulo="isEditingLinha ? ('Editar Linha ' + (modalLinha.codigoEstruturado || '')) : 'Adicionar Linha'"
    tamanho="lg"
    cor-header="var(--bs-primary)"
    @confirmar="salvarLinha"
    type-button="button"
  >
    <div class="row g-3">
      <div class="col-md-8">
        <CampoInput
          id="nome"
          label="Nome da Linha"
          v-model="modalLinha.nome"
          placeholder="Ex: Receita Operacional Líquida"
          maxlength="100"
          :required="true"
        />
      </div>

      <div class="col-md-4">
        <CampoInput
          id="codigoEstruturado"
          label="Código Estruturado"
          v-model="modalLinha.codigoEstruturado"
          placeholder="Ex: 1.1.01"
          maxlength="20"
          descricao-informativa="Opcional. Só faz sentido se o layout tiver máscara."
        />
      </div>

      <div class="col-md-4">
        <CampoBuscaLista
          id="lstTipoLinha"
          label="Tipo da Linha"
          v-model="modalLinha.lstTipoLinha"
          tabela="DreLinha"
          :required="true"
          descricao-informativa="ANALITICA = vinculada a categoria. SINTETICA = agrupa filhos. TOTALIZADOR = calculado."
          @show-notification="p => emit('show-notification', p)"
        />
      </div>

      <div class="col-md-4">
        <CampoBuscaLista
          id="lstNaturezaLinha"
          label="Natureza da Linha"
          v-model="modalLinha.lstNaturezaLinha"
          tabela="DreLinha"
          :required="true"
          descricao-informativa="RECEITA = positivo. DESPESA = negativo. AUTOMATICO = herda da categoria."
          @show-notification="p => emit('show-notification', p)"
        />
      </div>

      <div class="col-md-2">
        <label class="form-label">Nível</label>
        <input type="text" class="form-control" :value="'Nível ' + (modalLinha.nivel || 1)" disabled>
      </div>

      <div class="col-md-2">
        <label class="form-label">Ordem</label>
        <input type="number" class="form-control" v-model.number="modalLinha.ordem" min="1">
      </div>

      <div class="col-md-12" v-if="isLinhaAnalitica">
        <CampoBusca
          id="idCategoriaFinanceira"
          label="Categoria Financeira"
          v-model="modalLinha.idCategoriaFinanceira"
          :configuracao-busca="configBuscaCategoria"
          @show-notification="p => emit('show-notification', p)"
        />
      </div>

      <div class="col-md-12">
        <div class="border rounded p-3">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="exibirNoRelatorio" v-model="modalLinha.exibirNoRelatorio">
            <label class="form-check-label" for="exibirNoRelatorio">
              <strong>Exibir no Relatório</strong>
            </label>
          </div>
          <small class="text-muted d-block mt-1">
            <i class="bi bi-info-circle me-1"></i>
            Se desativado, a linha não aparece no relatório (apenas estrutural).
          </small>
        </div>
      </div>
    </div>

    <!-- Informação do pai (Vinculação Hierárquica) -->
    <div class="card mt-3 border-secondary" v-if="modalLinha.idDreLinhaPai">
      <div class="card-header bg-body-tertiary py-2">
        <h6 class="mb-0"><i class="bi bi-diagram-2 me-1 text-primary"></i> Vinculação Hierárquica</h6>
      </div>
      <div class="card-body py-2">
        <div class="mb-1">
          <span class="badge bg-light text-dark border font-monospace ms-2 me-2">
            {{ modalLinha.codigoEstruturadoPai || '---' }}
          </span>
          <span class="small text-muted" v-if="modalLinha.nomeLinhaPai">
            — {{ modalLinha.nomeLinhaPai }}
          </span>
        </div>
      </div>
    </div>
  </ModalFormularioV2>

  <!-- ============================================================ -->
  <!-- MODAL: PREVIEW DO RELATÓRIO DRE -->
  <!-- ============================================================ -->
  <ModalFormularioV2
    v-model="isPreviewOpen"
    titulo="Preview do Relatório DRE"
    tamanho="xl"
    cor-header="var(--bs-primary)"
  >
    <div class="table-responsive">
      <table class="table table-sm table-hover mb-0">
        <thead class="table-light">
          <tr>
            <th style="width: 120px;">Código</th>
            <th>Linha</th>
            <th style="width: 120px;">Tipo</th>
            <th style="width: 120px;">Natureza</th>
            <th style="width: 60px;" class="text-center">Visível</th>
            <th style="width: 150px;" class="text-end">Valor (R$)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="linha in linhasParaPreview" :key="linha.idDreLinha"
              :class="linha.lstTipoLinha === 'TOTALIZADOR' ? 'table-warning fw-bold' : ''">
            <td>
              <code class="small" :style="{ paddingLeft: ((linha.nivel - 1) * 16) + 'px' }">
                {{ linha.codigoEstruturado || '-' }}
              </code>
            </td>
            <td>{{ linha.nome }}</td>
            <td>
              <span class="badge" :class="getTipoLinhaBadgeClass(linha.lstTipoLinha)">
                {{ getTextoLista(listas.LstTipoLinha, linha.lstTipoLinha) || linha.lstTipoLinha }}
              </span>
            </td>
            <td>
              <span class="badge" :class="getNaturezaBadgeClass(linha.lstNaturezaLinha)">
                {{ getTextoLista(listas.LstNaturezaLinha, linha.lstNaturezaLinha) || linha.lstNaturezaLinha }}
              </span>
            </td>
            <td class="text-center">
              <i v-if="linha.exibirNoRelatorio" class="bi bi-eye-fill text-success"></i>
              <i v-else class="bi bi-eye-slash-fill text-warning"></i>
            </td>
            <td class="text-end text-muted small">—</td>
          </tr>
        </tbody>
      </table>
    </div>
    <template #footer>
      <div class="d-flex justify-content-between align-items-center w-100">
        <small class="text-muted">Total de linhas: {{ linhasParaPreview.length }}</small>
        <button type="button" class="btn btn-outline-secondary" @click="isPreviewOpen = false">Fechar</button>
      </div>
    </template>
  </ModalFormularioV2>

  <!-- ============================================================ -->
  <!-- AUDITORIA -->
  <!-- ============================================================ -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="DreLayout"
    :chave-primaria="item.idDreLayout"
    :titulo-registro="item.nome"
    contexto="tenant"
    :dicionarios="{
      'LstStatusLayout': listas.LstStatusLayout || []
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.tree-node-container {
  border-bottom: 1px solid var(--bs-border-color);
}

.tree-node:hover {
  background-color: var(--bs-tertiary-bg);
}

.tree-node[draggable="true"] {
  cursor: grab;
}

.tree-node[draggable="true"]:active {
  cursor: grabbing;
}
</style>
